/**
 * Industry Partner (DuDi) Assessment Page
 * 
 * Professional interface with:
 * - 20 focused questions on collaboration effectiveness
 * - N/A option for irrelevant questions
 * - Professional design language
 * - Progress tracking
 * - Auto-save functionality
 * - 5-7 minute estimated completion time
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { assessmentService, schoolService } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useAutoSave, useLoadSavedData, useBackgroundSync } from '../hooks';
import SchoolSelector from '../components/SchoolSelector';

const IndustryPartnerAssessment = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  // Refs
  const questionScrollRef = useRef(null);
  
  // State
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState('');

  // School selection state
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showSchoolSelector, setShowSchoolSelector] = useState(true);

  // Storage key for this assessment
  const storageKey = `industry_partner_assessment_${profile?.id}`;

  // Load saved data from localStorage
  const { savedData, clearSavedData } = useLoadSavedData(storageKey);

  // Auto-save to localStorage (500ms debounce)
  const { isSaving, lastSaved } = useAutoSave(storageKey, {
    responses,
    currentIndex,
    sessionId,
  }, 500);

  // Background sync to database (5 minutes)
  const { isSyncing } = useBackgroundSync(
    storageKey,
    async (data) => {
      if (sessionId) {
        await assessmentService.autoSave(sessionId, data);
      }
    },
    300000
  );

  // Rating scale with N/A option
  const ratingScale = [
    { value: 1, label: 'Sangat Tidak Setuju', color: 'bg-red-50 hover:bg-red-100 border-red-200' },
    { value: 2, label: 'Tidak Setuju', color: 'bg-orange-50 hover:bg-orange-100 border-orange-200' },
    { value: 3, label: 'Netral', color: 'bg-gray-50 hover:bg-gray-100 border-gray-200' },
    { value: 4, label: 'Setuju', color: 'bg-blue-50 hover:bg-blue-100 border-blue-200' },
    { value: 5, label: 'Sangat Setuju', color: 'bg-green-50 hover:bg-green-100 border-green-200' },
    { value: 0, label: 'Tidak Berlaku', color: 'bg-purple-50 hover:bg-purple-100 border-purple-200', isNA: true }
  ];

  // Load questions when school is selected
  useEffect(() => {
    if (selectedSchoolId) {
      loadQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolId]);

  // Restore saved progress
  useEffect(() => {
    if (savedData && savedData.responses) {
      setResponses(savedData.responses);
      setCurrentIndex(savedData.currentIndex || 0);
      setSessionId(savedData.sessionId);
    }
  }, [savedData]);

  // Handle school selection
  const handleSchoolSelect = async (schoolId) => {
    try {
      const { school, error } = await schoolService.getSchool(schoolId);
      if (error) {
        console.error('Error fetching school:', error);
        window.alert('Gagal memuat data sekolah. Silakan coba lagi.');
        return;
      }

      // Validate saved session belongs to the selected school
      if (savedData?.sessionId) {
        const { session } = await assessmentService.getSession(savedData.sessionId);
        const savedSchoolId = session?.target_school_id;
        if (savedSchoolId && String(savedSchoolId) !== String(schoolId)) {
          // Clear stale session data from different school
          clearSavedData();
          setResponses({});
          setSessionId(null);
          setCurrentIndex(0);
        }
      }

      setSelectedSchool(school);
      setSelectedSchoolId(schoolId);
      setShowSchoolSelector(false);
      setLoading(true);
    } catch (err) {
      console.error('Exception in handleSchoolSelect:', err);
      window.alert('Terjadi kesalahan. Silakan coba lagi.');
    }
  };

  const loadQuestions = async () => {
    try {
      const { questions: data, error } = await assessmentService.getQuestions('industry_partner');
      
      if (error) throw error;
      
      setQuestions(data);

      // Create session if not exists
      if (!sessionId && !savedData?.sessionId) {
        const { session, error: sessionError } = await assessmentService.createSession({
          user_id: profile?.id,
          total_questions: data.length,
          is_anonymous: false,
          target_school_id: selectedSchoolId, // Target school selected by user
          auto_save_data: { role: 'industry_partner' } // Store role for dashboard tracking
        });
        
        if (!sessionError) {
          setSessionId(session.id);
        }
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      window.alert('Gagal memuat pertanyaan. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  const handleRating = async (rating) => {
    const question = questions[currentIndex];
    
    const newResponses = {
      ...responses,
      [question.id]: {
        question_id: question.id,
        rating_value: rating,
        question_text: question.question_text,
        is_na: rating === 0
      }
    };
    
    setResponses(newResponses);

    // Auto-advance to next question after brief delay
    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        handleNext();
      }
    }, 300);
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      scrollToTop();
      checkMilestone(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      scrollToTop();
    }
  };

  const scrollToTop = () => {
    if (questionScrollRef.current) {
      questionScrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const checkMilestone = (index) => {
    const progress = ((index + 1) / questions.length) * 100;
    
    if (progress === 25) {
      showMilestone('Awal yang Bagus! 🎯', '25% Selesai');
    } else if (progress === 50) {
      showMilestone('Setengah Jalan! 💼', '50% Selesai');
    } else if (progress === 75) {
      showMilestone('Hampir Selesai! 🌟', '75% Selesai');
    }
  };

  const showMilestone = (message, subtitle) => {
    setCelebrationMessage(`${message}\n${subtitle}`);
    setShowCelebration(true);
    setTimeout(() => setShowCelebration(false), 2000);
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      window.alert('Harap jawab semua pertanyaan sebelum mengirim.');
      return;
    }

    setSubmitting(true);

    try {
      const responsesArray = Object.values(responses).map(r => ({
        session_id: sessionId,
        question_id: r.question_id,
        user_id: profile?.id,
        response_value: {
          rating: r.is_na ? null : r.rating_value,
          is_na: r.is_na || false
        }
      }));

      const { error: submitError } = await assessmentService.submitResponses(responsesArray);
      
      if (submitError) throw submitError;

      // Mark session as completed
      const { error: completeError } = await assessmentService.completeSession(sessionId);
      
      if (completeError) throw completeError;

      // Clear saved data
      clearSavedData();
      
      // Show success and redirect
      window.alert('Terima kasih telah menyelesaikan penilaian! Masukan Anda sangat berharga.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting assessment:', error);
      window.alert('Gagal mengirim penilaian. Silakan coba lagi.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndExit = () => {
    if (window.confirm('Progress Anda telah disimpan. Anda dapat melanjutkan penilaian ini nanti dari dashboard Anda.')) {
      navigate('/dashboard');
    }
  };

  const canSubmit = () => {
    return questions.every(q => responses[q.id]);
  };

  const getProgress = () => {
    const answered = Object.keys(responses).length;
    return (answered / questions.length) * 100;
  };

  const getAnsweredCount = () => {
    return Object.keys(responses).length;
  };

  // Show school selector modal first
  if (showSchoolSelector) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Pilih Sekolah</h2>
            <p className="text-gray-600 mt-2">
              Pilih sekolah mitra yang akan Anda nilai kepala sekolahnya
            </p>
          </div>

          <div className="mb-6">
            <SchoolSelector
              value={null}
              onChange={handleSchoolSelect}
              label="Sekolah Mitra"
              placeholder="Pilih sekolah..."
              required={true}
            />
          </div>

          <div className="text-center text-sm text-gray-500">
            Anda akan melakukan penilaian untuk kepala sekolah yang dipilih
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat penilaian...</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* School Info Banner */}
      {selectedSchool && (
        <div className="bg-blue-600 text-white py-2 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="font-medium">{selectedSchool.nama}</span>
              <span className="text-blue-200">| NPSN: {selectedSchool.npsn}</span>
            </div>
            <button
              onClick={() => {
                setResponses({});
                setSessionId(null);
                setCurrentIndex(0);
                clearSavedData();
                setShowSchoolSelector(true);
                setSelectedSchoolId(null);
                setSelectedSchool(null);
              }}
              className="text-blue-200 hover:text-white text-sm underline"
            >
              Ganti Sekolah
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Penilaian Mitra Industri
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Evaluasi Kolaborasi & Kemitraan
              </p>
            </div>
            <button
              onClick={handleSaveAndExit}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Simpan & Keluar
            </button>
          </div>

          {/* Progress Bar */}
          <div className="relative">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Progres: {getAnsweredCount()} dari {questions.length}</span>
              <span>{Math.round(getProgress())}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-500"
                style={{ width: `${getProgress()}%` }}
              />
            </div>
          </div>

          {/* Save Status */}
          <div className="flex items-center justify-between mt-3 text-xs">
            <div className="flex items-center space-x-4">
              {isSaving && (
                <span className="text-blue-600 flex items-center">
                  <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Menyimpan...
                </span>
              )}
              {!isSaving && lastSaved && (
                <span className="text-green-600 flex items-center">
                  ✓ Progres tersimpan
                </span>
              )}
              {isSyncing && (
                <span className="text-purple-600">☁ Menyinkronkan ke server...</span>
              )}
            </div>
            <span className="text-gray-500">
              ⏱ Estimasi waktu: 5-7 menit
            </span>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="max-w-4xl mx-auto px-4 py-8" ref={questionScrollRef}>
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
          {/* Question Number */}
          <div className="text-sm font-semibold text-blue-600 mb-4">
            Pertanyaan {currentIndex + 1} dari {questions.length}
          </div>

          {/* Question Text */}
          <h2 className="text-2xl font-semibold text-gray-900 mb-8 leading-relaxed">
            {currentQuestion?.question_text}
          </h2>

          {/* Rating Options */}
          <div className="space-y-3 mb-8">
            {ratingScale.map((option) => (
              <button
                key={option.value}
                onClick={() => handleRating(option.value)}
                className={`
                  w-full p-4 rounded-lg border-2 text-left transition-all
                  ${responses[currentQuestion?.id]?.rating_value === option.value
                    ? 'border-blue-500 bg-blue-50 shadow-md scale-[1.02]'
                    : option.color
                  }
                  ${option.isNA ? 'border-dashed' : ''}
                `}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">
                    {option.label}
                  </span>
                  {responses[currentQuestion?.id]?.rating_value === option.value && (
                    <span className="text-blue-600">✓</span>
                  )}
                </div>
                {option.isNA && (
                  <p className="text-xs text-gray-500 mt-1">
                    Pilih jika pertanyaan ini tidak berlaku untuk kolaborasi Anda
                  </p>
                )}
              </button>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-6 border-t">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`
                px-6 py-3 rounded-lg font-medium transition-all
                ${currentIndex === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }
              `}
            >
              ← Sebelumnya
            </button>

            {currentIndex === questions.length - 1 ? (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit() || submitting}
                className={`
                  px-8 py-3 rounded-lg font-semibold transition-all
                  ${canSubmit() && !submitting
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                {submitting ? 'Mengirim...' : 'Kirim Penilaian'}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!responses[currentQuestion?.id]}
                className={`
                  px-6 py-3 rounded-lg font-medium transition-all
                  ${responses[currentQuestion?.id]
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }
                `}
              >
                Selanjutnya →
              </button>
            )}
          </div>

          {/* Helper Text */}
          {!responses[currentQuestion?.id] && (
            <p className="text-center text-sm text-gray-500 mt-4">
              Silakan pilih jawaban untuk melanjutkan
            </p>
          )}
        </div>
      </div>

      {/* Milestone Celebration */}
      {showCelebration && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center animate-bounce border-4 border-blue-500">
            <p className="text-3xl font-bold text-gray-900 whitespace-pre-line">
              {celebrationMessage}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndustryPartnerAssessment;
